import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import {
  AlertTriangle,
  Clock3,
  History,
  Loader2,
  Plus,
  Save,
  Search,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { ADMIN_PERMISSIONS } from '../../constants';
import { adminAPI } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { hasPermission } from '../../utils/rbac';

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' }
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const defaultAnswerTypes = ['text', 'textarea', 'number', 'boolean', 'single_select', 'multi_select', 'scale', 'ranking', 'text_array', 'object_list'];
const defaultInputTypes = ['text', 'textarea', 'number', 'toggle', 'select', 'radio', 'checkbox', 'slider', 'ranking', 'tag_input', 'repeatable_group'];

const emptyForm = {
  id: null,
  questionKey: '',
  category: '',
  section: '',
  prompt: '',
  helpText: '',
  answerType: 'textarea',
  inputType: 'textarea',
  required: false,
  active: true,
  choicesJson: '[]',
  validationRulesJson: '{}',
  metadataJson: '{}',
  displayOrder: '',
  versionMode: 'auto',
  acknowledgeAnsweredImpact: false,
  changeSummary: ''
};

const extractError = (error, fallback) => error?.response?.data?.error || error?.response?.error || error?.message || fallback;

const labelize = (value = '') => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const parseJsonField = (value, fallback, label) => {
  if (!String(value || '').trim()) return fallback;
  try {
    return JSON.parse(value);
  } catch (_error) {
    throw new Error(`${label} must be valid JSON.`);
  }
};

const prettyJson = (value, fallback) => {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch (_error) {
    return JSON.stringify(fallback, null, 2);
  }
};

const statusTone = (active) => active
  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
  : 'border-slate-200 bg-slate-50 text-slate-600';

const fieldClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100';

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const MetricTile = ({ label, value, detail }) => (
  <article className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-6">
      <span className="text-sm font-semibold text-slate-600">{label}</span>
    </div>
    <div>
      <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
    </div>
  </article>
);

const Modal = ({ title, subtitle, children, onClose, width = 'max-w-4xl' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
    <div className={`max-h-[92vh] w-full ${width} overflow-hidden rounded-2xl bg-white shadow-2xl`}>
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>
      </div>
      <div className="max-h-[calc(92vh-82px)] overflow-y-auto px-5 py-5">
        {children}
      </div>
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading question banks">
    <div className="mb-6 space-y-3">
      <SkeletonBlock className="h-4 w-48 rounded-md" />
      <SkeletonBlock className="h-10 w-80 rounded-lg" />
      <SkeletonBlock className="h-4 w-[46rem] max-w-full rounded-md" />
    </div>
    <div className="mb-5 grid gap-3 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => <SkeletonBlock key={index} className="h-32 rounded-[24px]" />)}
    </div>
    <SkeletonBlock className="mb-4 h-16 rounded-[24px]" />
    <div className="space-y-3">
      {Array.from({ length: 8 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-[24px]" />)}
    </div>
  </div>
);

const buildFormFromQuestion = (question) => ({
  id: question.id,
  questionKey: question.questionKey || '',
  category: question.category || '',
  section: question.section || '',
  prompt: question.prompt || '',
  helpText: question.helpText || '',
  answerType: question.answerType || 'textarea',
  inputType: question.inputType || 'textarea',
  required: Boolean(question.required),
  active: Boolean(question.active),
  choicesJson: prettyJson(question.choices, []),
  validationRulesJson: prettyJson(question.validationRules, {}),
  metadataJson: prettyJson(question.metadata, {}),
  displayOrder: question.displayOrder ?? '',
  versionMode: question.hasAnswers ? 'new_version' : 'in_place',
  acknowledgeAnsweredImpact: false,
  changeSummary: ''
});

const BankCard = ({ bank, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(bank.key)}
    className={`group rounded-[24px] border bg-white p-4 text-left transition hover:border-slate-400 hover:shadow-sm ${
      active ? 'border-slate-950 shadow-sm ring-1 ring-slate-950' : 'border-slate-200 shadow-sm'
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">{bank.label}</p>
        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{bank.description}</p>
      </div>
      <span className={`mt-1 h-2.5 w-2.5 rounded-full ${active ? 'bg-slate-950' : 'bg-slate-300 group-hover:bg-slate-500'}`} />
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
      <div className="rounded-lg bg-slate-50 px-2 py-2">
        <div className="text-lg font-semibold text-slate-900">{bank.activeQuestions || 0}</div>
        <div className="text-[11px] font-medium text-slate-500">Active</div>
      </div>
      <div className="rounded-lg bg-slate-50 px-2 py-2">
        <div className="text-lg font-semibold text-slate-900">{bank.answeredQuestions || 0}</div>
        <div className="text-[11px] font-medium text-slate-500">Answered</div>
      </div>
      <div className="rounded-lg bg-slate-50 px-2 py-2">
        <div className="text-lg font-semibold text-slate-900">{bank.totalAnswers || 0}</div>
        <div className="text-[11px] font-medium text-slate-500">Answers</div>
      </div>
    </div>
  </button>
);

const PaginationControls = ({ pagination, onPageChange, onLimitChange }) => {
  const page = Number(pagination?.page || 1);
  const limit = Number(pagination?.limit || PAGE_SIZE_OPTIONS[0]);
  const total = Number(pagination?.total || 0);
  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const start = total ? ((page - 1) * limit) + 1 : 0;
  const end = total ? Math.min(page * limit, total) : 0;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <div>
        Showing <span className="font-semibold text-slate-900">{start}-{end}</span> of <span className="font-semibold text-slate-900">{total}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={limit}
          onChange={(event) => onLimitChange(Number(event.target.value))}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
        >
          {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
        </select>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const QuestionFormModal = ({ bank, question, answerTypes, inputTypes, onClose, onSaved }) => {
  const isEdit = Boolean(question?.id);
  const [form, setForm] = useState(isEdit ? buildFormFromQuestion(question) : emptyForm);
  const [saving, setSaving] = useState(false);
  const answeredEdit = isEdit && Number(question.answerCount || 0) > 0;

  const setValue = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const buildPayload = () => {
    const choices = parseJsonField(form.choicesJson, [], 'Choices');
    const validationRules = parseJsonField(form.validationRulesJson, {}, 'Validation rules');
    const metadata = parseJsonField(form.metadataJson, {}, 'Metadata');

    return {
      questionKey: form.questionKey.trim(),
      category: form.category.trim(),
      section: form.section.trim(),
      prompt: form.prompt.trim(),
      helpText: form.helpText.trim() || null,
      answerType: form.answerType,
      inputType: form.inputType,
      required: Boolean(form.required),
      active: Boolean(form.active),
      choices,
      validationRules,
      metadata,
      displayOrder: form.displayOrder === '' ? null : Number(form.displayOrder),
      versionMode: isEdit ? form.versionMode : undefined,
      acknowledgeAnsweredImpact: Boolean(form.acknowledgeAnsweredImpact),
      changeSummary: form.changeSummary.trim()
    };
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await adminAPI.updateQuestionBankQuestion(bank, question.id, payload);
        toast.success(payload.versionMode === 'new_version' ? 'Question version created.' : 'Question updated.');
      } else {
        await adminAPI.createQuestionBankQuestion(bank, payload);
        toast.success('Question created.');
      }
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Question save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit question' : 'Create question'}
      subtitle={isEdit ? 'Answered questions are protected by versioning and snapshots.' : 'Create a governed question for the selected bank.'}
      onClose={onClose}
      width="max-w-5xl"
    >
      <form onSubmit={save} className="space-y-5">
        {answeredEdit && (
          <div className="rounded-[24px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 shrink-0" size={18} />
              <div>
                <p className="font-semibold">This question has {question.answerCount} saved answers.</p>
                <p>Use a new version for prompt, key, type, choice, required, or validation changes. Existing answers keep their original question snapshot.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Question key</span>
            <input
              value={form.questionKey}
              onChange={(event) => setValue('questionKey', event.target.value)}
              required
              className={`mt-1 ${fieldClass}`}
              placeholder="candidate_portfolio_proof"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Display order</span>
            <input
              type="number"
              value={form.displayOrder}
              onChange={(event) => setValue('displayOrder', event.target.value)}
              className={`mt-1 ${fieldClass}`}
              placeholder="Leave blank to append"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Category</span>
            <input
              value={form.category}
              onChange={(event) => setValue('category', event.target.value)}
              required
              className={`mt-1 ${fieldClass}`}
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Section</span>
            <input
              value={form.section}
              onChange={(event) => setValue('section', event.target.value)}
              required
              className={`mt-1 ${fieldClass}`}
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Prompt</span>
          <textarea
            value={form.prompt}
            onChange={(event) => setValue('prompt', event.target.value)}
            required
            rows={3}
            className={`mt-1 ${fieldClass} leading-6`}
          />
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Help text</span>
          <textarea
            value={form.helpText}
            onChange={(event) => setValue('helpText', event.target.value)}
            rows={2}
            className={`mt-1 ${fieldClass} leading-6`}
          />
        </label>

        <div className="grid gap-4 lg:grid-cols-4">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Answer type</span>
            <select
              value={form.answerType}
              onChange={(event) => setValue('answerType', event.target.value)}
              className={`mt-1 ${fieldClass}`}
            >
              {(answerTypes || defaultAnswerTypes).map((type) => <option key={type} value={type}>{labelize(type)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Input type</span>
            <select
              value={form.inputType}
              onChange={(event) => setValue('inputType', event.target.value)}
              className={`mt-1 ${fieldClass}`}
            >
              {(inputTypes || defaultInputTypes).map((type) => <option key={type} value={type}>{labelize(type)}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(event) => setValue('required', event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            />
            <span className="text-sm font-semibold text-slate-700">Required</span>
          </label>
          <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => setValue('active', event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            />
            <span className="text-sm font-semibold text-slate-700">Active</span>
          </label>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Choices JSON</span>
            <textarea
              value={form.choicesJson}
              onChange={(event) => setValue('choicesJson', event.target.value)}
              rows={8}
              spellCheck={false}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs leading-5 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Validation rules JSON</span>
            <textarea
              value={form.validationRulesJson}
              onChange={(event) => setValue('validationRulesJson', event.target.value)}
              rows={8}
              spellCheck={false}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs leading-5 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Metadata JSON</span>
            <textarea
              value={form.metadataJson}
              onChange={(event) => setValue('metadataJson', event.target.value)}
              rows={8}
              spellCheck={false}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs leading-5 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>
        </div>

        {isEdit && (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Version mode</span>
                <select
	                  value={form.versionMode}
	                  onChange={(event) => setValue('versionMode', event.target.value)}
	                  className={`mt-1 ${fieldClass}`}
	                >
                  <option value="auto">Auto decide</option>
                  <option value="new_version">Create new version</option>
                  <option value="in_place">Update current version</option>
                </select>
              </label>
              {answeredEdit && form.versionMode === 'in_place' && (
                <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-white px-3 py-3">
                  <input
	                    type="checkbox"
	                    checked={form.acknowledgeAnsweredImpact}
	                    onChange={(event) => setValue('acknowledgeAnsweredImpact', event.target.checked)}
	                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
	                  />
                  <span className="text-sm leading-6 text-slate-600">
                    I understand this can change the active wording for users who already answered this question. Existing answer snapshots remain preserved for audit.
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Change summary</span>
          <textarea
            value={form.changeSummary}
            onChange={(event) => setValue('changeSummary', event.target.value)}
            required
            rows={2}
            className={`mt-1 ${fieldClass} leading-6`}
            placeholder="Explain why this question is being added or changed."
          />
        </label>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save question
          </button>
        </div>
      </form>
    </Modal>
  );
};

const HistoryModal = ({ bank, question, onClose }) => {
  const historyQuery = useQuery(
    ['question-bank-history', bank, question?.id],
    () => adminAPI.getQuestionBankHistory(bank, question.id),
    { enabled: Boolean(bank && question?.id), staleTime: 15000 }
  );

  const versions = historyQuery.data?.versions || [];
  const revisions = historyQuery.data?.revisions || [];

  return (
    <Modal
      title="Question history"
      subtitle={`${question.questionKey} keeps old answers attached to their original version.`}
      onClose={onClose}
      width="max-w-5xl"
    >
      {historyQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => <SkeletonBlock key={index} className="h-24 rounded-[24px]" />)}
        </div>
      ) : historyQuery.isError ? (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {extractError(historyQuery.error, 'Question history could not be loaded.')}
        </div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Versions</h3>
            <div className="space-y-3">
              {versions.map((version) => (
                <article key={version.id} className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <Chip className={statusTone(version.active)}>{version.active ? 'Active' : 'Retired'}</Chip>
                    <span className="text-xs font-semibold text-slate-500">Version {version.versionNumber}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-slate-900">{version.prompt}</p>
                  <p className="mt-2 text-xs text-slate-500">{version.answerCount} saved answers</p>
                  <p className="mt-2 text-xs text-slate-400">Updated {formatDateTime(version.updatedAt)}</p>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Revision Audit</h3>
            <div className="space-y-3">
              {revisions.map((revision) => (
                <article key={revision.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-center gap-2">
	                    <Chip className="border-slate-200 bg-white text-slate-700">{labelize(revision.revisionType)}</Chip>
                    <span className="text-xs font-semibold text-slate-500">Version {revision.versionNumber}</span>
                    <span className="text-xs text-slate-400">{formatDateTime(revision.createdAt)}</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{revision.changeSummary}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    Changed by {revision.changedBy?.name || 'system migration'}
                  </p>
                </article>
              ))}
              {!revisions.length && (
                <div className="rounded-[24px] border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                  No revision history recorded for this question.
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
};

function QuestionBanksPage() {
  const currentUser = useAuthStore((state) => state.user);
  const canManage = hasPermission(currentUser, ADMIN_PERMISSIONS.QUESTION_BANKS_MANAGE);
  const [selectedBank, setSelectedBank] = useState('candidate_profile');
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [historyQuestion, setHistoryQuestion] = useState(null);

  const questionBanksQuery = useQuery(
    ['question-banks', selectedBank, status, search, page, limit],
    () => adminAPI.getQuestionBanks({ bank: selectedBank, status, search, page, limit }),
    { staleTime: 20000 }
  );

  const banks = useMemo(() => questionBanksQuery.data?.banks || [], [questionBanksQuery.data?.banks]);
  const questions = useMemo(() => questionBanksQuery.data?.questions || [], [questionBanksQuery.data?.questions]);
  const answerTypes = questionBanksQuery.data?.answerTypes || defaultAnswerTypes;
  const inputTypes = questionBanksQuery.data?.inputTypes || defaultInputTypes;
  const selectedBankMeta = useMemo(
    () => banks.find((bank) => bank.key === selectedBank),
    [banks, selectedBank]
  );
  const pagination = questionBanksQuery.data?.pagination || {
    page,
    limit,
    total: questions.length,
    totalPages: 1
  };
  const visibleRequiredCount = questions.filter((question) => question.required).length;
  const visibleAnsweredCount = questions.filter((question) => question.hasAnswers).length;

  const refresh = () => questionBanksQuery.refetch();
  const applySearch = () => {
    setPage(1);
    setSearch(draftSearch.trim());
  };
  const updateStatus = (nextStatus) => {
    setPage(1);
    setStatus(nextStatus);
  };
  const updateLimit = (nextLimit) => {
    setPage(1);
    setLimit(nextLimit);
  };

  if (questionBanksQuery.isLoading && !questionBanksQuery.data) return <PageSkeleton />;

  return (
    <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Question Banks"
        description="Manage candidate, employer, and job matching questions with answer-safe versioning. Meaningful edits create new active versions so old answers keep their original snapshot."
        actions={(
          canManage && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <Plus size={16} />
              Create question
            </button>
          )
        )}
      />

      <section className="mb-5 grid gap-3 lg:grid-cols-3">
        {banks.map((bank) => (
          <BankCard
            key={bank.key}
            bank={bank}
	            active={bank.key === selectedBank}
	            onSelect={(key) => {
	              setSelectedBank(key);
	              setSearch('');
	              setDraftSearch('');
	              setPage(1);
	            }}
	          />
	        ))}
	      </section>

	      <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
	        <MetricTile label="Questions" value={selectedBankMeta?.totalQuestions || 0} detail={`${selectedBankMeta?.activeQuestions || 0} active, ${selectedBankMeta?.retiredQuestions || 0} retired`} />
	        <MetricTile label="Answered" value={selectedBankMeta?.answeredQuestions || 0} detail={`${selectedBankMeta?.totalAnswers || 0} total saved answers`} />
	        <MetricTile label="Visible" value={pagination.total || questions.length} detail={`${visibleRequiredCount} required in current page`} />
	        <MetricTile label="Version Risk" value={visibleAnsweredCount} detail="Visible questions with answer snapshots" />
	      </section>

	      <section className="mb-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
	        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_150px_auto]">
	          <label className="relative block">
	            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
	            <input
	              value={draftSearch}
	              onChange={(event) => setDraftSearch(event.target.value)}
	              onKeyDown={(event) => {
	                if (event.key === 'Enter') applySearch();
	              }}
	              className={`${fieldClass} pl-10`}
	              placeholder="Search by key, prompt, category, or section"
	            />
	          </label>
	          <select
	            value={status}
	            onChange={(event) => updateStatus(event.target.value)}
	            className={fieldClass}
	          >
	            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
	          </select>
	          <select
	            value={limit}
	            onChange={(event) => updateLimit(Number(event.target.value))}
	            className={fieldClass}
	          >
	            {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
	          </select>
	          <button
	            type="button"
	            onClick={applySearch}
	            className="inline-flex items-center justify-center rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
	          >
	            Apply filters
	          </button>
	        </div>
	      </section>

	      {!canManage && (
	        <div className="mb-5 rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700 shadow-sm">
	          You have view-only access. Question changes require the Question Bank Management permission.
	        </div>
	      )}

	      <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
	        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
	          <div>
	            <h2 className="text-lg font-semibold text-slate-900">{selectedBankMeta?.label || labelize(selectedBank)}</h2>
	            <p className="mt-1 text-sm text-slate-500">{pagination.total || 0} questions match the current filters</p>
	          </div>
	          <div className="flex flex-wrap gap-2">
	            <Chip className="border-slate-200 bg-slate-50 text-slate-600">
	              Snapshot protected
	            </Chip>
	            <Chip className="border-slate-200 bg-slate-50 text-slate-600">
	              Versioned
	            </Chip>
	          </div>
	        </div>

	        {questionBanksQuery.isFetching && (
	          <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold text-slate-700">
	            Updating question bank data...
	          </div>
	        )}

	        <div className="overflow-x-auto">
	          <table className="min-w-[1180px] divide-y divide-slate-100 text-left text-sm">
	            <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
	              <tr>
	                <th className="px-6 py-4">Order</th>
	                <th className="px-6 py-4">Question</th>
	                <th className="px-6 py-4">Category</th>
	                <th className="px-6 py-4">Type</th>
	                <th className="px-6 py-4">Version</th>
	                <th className="px-6 py-4">Answers</th>
	                <th className="px-6 py-4">Updated</th>
	                <th className="px-6 py-4 text-right">Actions</th>
	              </tr>
	            </thead>
	            <tbody className="divide-y divide-slate-100 bg-white">
	              {questions.map((question) => (
	                <tr key={question.id} className="hover:bg-slate-50">
	                  <td className="px-6 py-4 font-mono text-xs text-slate-600">{question.displayOrder}</td>
	                  <td className="px-6 py-4">
	                    <p className="max-w-[420px] truncate font-medium text-slate-900">{question.prompt}</p>
	                    <p className="mt-1 max-w-[420px] truncate font-mono text-xs text-slate-500">{question.questionKey}</p>
	                    {question.lastChangeSummary && <p className="mt-2 max-w-[420px] truncate text-xs text-slate-500">{question.lastChangeSummary}</p>}
	                  </td>
	                  <td className="px-6 py-4">
	                    <p className="font-medium text-slate-800">{labelize(question.category)}</p>
	                    <p className="mt-1 text-xs text-slate-500">{labelize(question.section)}</p>
	                  </td>
	                  <td className="px-6 py-4">
	                    <p className="text-slate-700">{labelize(question.answerType)}</p>
	                    <p className="mt-1 text-xs text-slate-500">{labelize(question.inputType)}</p>
	                    {question.required && <Chip className="mt-2 border-slate-200 bg-slate-50 text-slate-700">Required</Chip>}
	                  </td>
	                  <td className="px-6 py-4">
	                    <Chip className={statusTone(question.active)}>{question.active ? 'Active' : 'Retired'}</Chip>
	                    <p className="mt-2 text-xs font-medium text-slate-500">Version {question.versionNumber}</p>
	                  </td>
	                  <td className="px-6 py-4">
	                    <p className="font-semibold text-slate-900">{question.answerCount || 0}</p>
	                    <p className="mt-1 text-xs text-slate-500">{question.hasAnswers ? 'Snapshot locked' : 'No answers'}</p>
	                  </td>
	                  <td className="px-6 py-4 text-slate-600">{formatDateTime(question.updatedAt || question.lastRevisedAt)}</td>
	                  <td className="px-6 py-4">
	                    <div className="flex justify-end gap-2">
	                      <button
	                        type="button"
	                        onClick={() => setHistoryQuestion(question)}
	                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
	                      >
	                        <History size={15} />
	                        History
	                      </button>
	                      {canManage && (
	                        <button
	                          type="button"
	                          onClick={() => setEditingQuestion(question)}
	                          className="inline-flex items-center justify-center rounded-lg bg-slate-950 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
	                        >
	                          Edit
	                        </button>
	                      )}
	                    </div>
	                  </td>
	                </tr>
	              ))}
	              {!questions.length && (
	                <tr>
	                  <td colSpan={8} className="px-4 py-16 text-center">
	                    <Clock3 className="mx-auto text-slate-300" size={36} />
	                    <h3 className="mt-3 text-sm font-semibold text-slate-900">No questions found</h3>
	                    <p className="mt-1 text-sm text-slate-500">Adjust the search or status filter for this question bank.</p>
	                  </td>
	                </tr>
	              )}
	            </tbody>
	          </table>
	        </div>
	        <PaginationControls
	          pagination={pagination}
	          onPageChange={(nextPage) => setPage(Math.min(Math.max(1, nextPage), Math.max(1, pagination.totalPages || 1)))}
	          onLimitChange={updateLimit}
	        />
	      </section>

      {showCreate && (
        <QuestionFormModal
          bank={selectedBank}
          answerTypes={answerTypes}
          inputTypes={inputTypes}
          onClose={() => setShowCreate(false)}
          onSaved={refresh}
        />
      )}

      {editingQuestion && (
        <QuestionFormModal
          bank={selectedBank}
          question={editingQuestion}
          answerTypes={answerTypes}
          inputTypes={inputTypes}
          onClose={() => setEditingQuestion(null)}
          onSaved={refresh}
        />
      )}

      {historyQuestion && (
        <HistoryModal
          bank={selectedBank}
          question={historyQuestion}
          onClose={() => setHistoryQuestion(null)}
        />
      )}
    </div>
  );
}

export default QuestionBanksPage;
